/**
 * Runtime theming.
 *
 * DesignDeck ships as one static bundle embedded by several shops, so the
 * look cannot be decided at build time. The stylesheet (src/styles/theme.css)
 * reads everything from CSS custom properties whose defaults are the
 * reference design; this module overwrites them at runtime from a customer's
 * branding — three base colours, see `@designdeck/shared` `brandingTokens`.
 *
 * Sources, in ascending priority:
 *   1. the defaults baked into theme.css (the reference design),
 *   2. the branding stored on the Customer (arrives with the logged-in user
 *      in the Seller Creator, with the session exchange in Direct Sale),
 *   3. iframe URL parameters (`?primaryColor=`, `?paperColor=`, `?inkColor=`)
 *      — per embed, so a shop can try a look without storing it. When the
 *      URL carries none of them, the values recorded at the Seller iframe
 *      start stand in (see lib/iframe-boot.ts): the auto-login redirect
 *      drops them from the URL, a reload never had them.
 *
 * `applyBranding` is called once before the first render with the URL
 * values, then again whenever a stored branding arrives; the URL values
 * win on every call, so the order of arrival never matters. Admin and auth
 * routes opt out of the URL and the recorded values with `ignoreUrl`, so
 * they keep the reference colours (see hooks/useBrandingSync.ts).
 */

import {
  brandingTokens,
  isHexColor,
  normalizeHex,
  type Branding,
} from '@designdeck/shared';

import { isPolarisEmbed } from './embed-theme';
import { readIframeParams, type IframeParamKey } from './iframe-boot';

export { isHexColor as isValidHexColor } from '@designdeck/shared';

/** URL parameter per branding key; `primaryColor` predates the other two. */
const URL_PARAMS: Record<keyof Branding, IframeParamKey> = {
  primary: 'primaryColor',
  paper: 'paperColor',
  ink: 'inkColor',
};

const BRANDING_KEYS = Object.keys(URL_PARAMS) as (keyof Branding)[];

/**
 * Reads the valid branding values off the current URL. When the URL carries
 * none of the three colour parameters, the values recorded at the Seller
 * iframe start are read instead; a URL with at least one colour parameter is
 * taken as the embedder's complete wish and the recorded values are ignored.
 */
export function brandingFromUrl(): Branding {
  if (typeof window === 'undefined') return {};
  const params = new URLSearchParams(window.location.search);
  const urlHasColour = BRANDING_KEYS.some((key) => params.has(URL_PARAMS[key]));
  const recorded = urlHasColour ? null : readIframeParams();

  const branding: Branding = {};
  for (const key of BRANDING_KEYS) {
    const name = URL_PARAMS[key];
    const raw = recorded ? recorded[name] : params.get(name);
    if (raw && isHexColor(raw)) branding[key] = normalizeHex(raw);
  }
  return branding;
}

/** Branding last handed in by the API, so a later call can re-merge it. */
let storedBranding: Branding | null = null;

export interface ApplyBrandingOptions {
  /**
   * Skip the URL parameters AND the values recorded at the iframe start, so
   * only the stored branding (if any) applies. Admin and auth routes use it
   * to stay in the reference colours whatever the embed asked for.
   */
  ignoreUrl?: boolean;
}

/**
 * Applies the merged branding as inline custom properties on <html>. The
 * stored branding is remembered; the URL overrides are re-read each time.
 * With nothing stored and nothing on the URL the inline properties are
 * removed again and the stylesheet defaults stand.
 */
export function applyBranding(
  stored?: Branding | null,
  options: ApplyBrandingOptions = {}
): void {
  if (stored !== undefined) storedBranding = stored;
  if (typeof document === 'undefined') return;

  // Under an embedding theme (lib/embed-theme.ts) the host's look wins: no
  // customer colours, the theme's stylesheet values stand.
  const merged: Branding = isPolarisEmbed()
    ? {}
    : {
        ...(storedBranding ?? {}),
        ...(options.ignoreUrl ? {} : brandingFromUrl()),
      };
  const root = document.documentElement.style;
  const hasAny = Object.values(merged).some((v) => isHexColor(v));
  for (const [name, value] of Object.entries(brandingTokens(merged))) {
    if (hasAny) root.setProperty(name, value);
    else root.removeProperty(name);
  }
}

/** Reads and validates `primaryColor` from the current URL, if present. */
export function getIframeThemeColor(): string | null {
  return brandingFromUrl().primary ?? null;
}

/**
 * Backwards-compatible entry: a primary colour alone. Kept for the callers
 * that only know the old contract; new code hands `applyBranding` the whole
 * branding.
 */
export function applyPrimaryTheme(seedHex: string | null): void {
  if (!seedHex || !isHexColor(seedHex)) return;
  applyBranding({ ...(storedBranding ?? {}), primary: normalizeHex(seedHex) });
}

function hexToRgbTuple(hex: string): [number, number, number] {
  const n = parseInt(normalizeHex(hex).slice(1), 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

/**
 * Reads a themed RGB-triplet custom property back out as an `rgb(...)` /
 * `rgba(...)` string, for the handful of spots (Fabric.js canvas config)
 * that need a literal colour rather than a class. Falls back to
 * `fallbackHex` when the property is unset or not a triplet. Pass `alpha`
 * (0-1) for a translucent result.
 */
export function getThemeColorVar(
  varName: string,
  fallbackHex: string,
  alpha?: number
): string {
  const fallback =
    alpha === undefined
      ? fallbackHex
      : `rgba(${hexToRgbTuple(fallbackHex).join(', ')}, ${alpha})`;
  if (typeof document === 'undefined') return fallback;

  const value = getComputedStyle(document.documentElement)
    .getPropertyValue(varName)
    .trim();
  if (!value) return fallback;
  const [r, g, b] = value.split(/\s+/).map(Number);
  if ([r, g, b].some((n) => Number.isNaN(n))) return fallback;

  return alpha === undefined
    ? `rgb(${r}, ${g}, ${b})`
    : `rgba(${r}, ${g}, ${b}, ${alpha})`;
}
